<template>
    <div
        v-qa.hydrated="'hydrated'"
        class="top-bar"
        :data-qa="'top-bar'"
    >
        <div class="top-bar-wrapper">
            <div class="top-bar-account">
                <ThButton
                    v-if="!isLogged"
                    :icon-leading="'profile'"
                    aria-label="Account"
                    class="account"
                    type="link"
                    @click="toggleLoginForm"
                >
                    {{ t('header.login_button') }}
                </ThButton>

                <ThButton
                    v-else
                    :icon-leading="'profile'"
                    :to="routes.designStudio()"
                    aria-label="Account"
                    class="account"
                    type="link"
                    @click="closeMenu"
                >
                    Profile
                </ThButton>
            </div>

            <div
                v-if="topBar && topBar.siteWideMessage"
                class="site-wide-message"
            >
                {{ topBar.siteWideMessage.message }}
                <span> | </span>
                <CmsLink
                    v-if="topBar.siteWideMessage.link"
                    :link="topBar.siteWideMessage.link"
                >
                    {{ topBar.siteWideMessage.link.text }}
                </CmsLink>
            </div>

            <CmsLink
                v-if="
                    topBar &&
                    topBar.consultationPrompt &&
                    topBar.consultationPrompt.link
                "
                :link="topBar.consultationPrompt.link"
                class="book-a-consultation"
            >
                {{ topBar.consultationPrompt.link.text }}
            </CmsLink>

            <div
                v-if="!hideSwitchers"
                class="switchers"
            >
                <CurrencySwitcher
                    v-qa="'currency-switcher'"
                    class="currency-switcher"
                />

                <RegionSwitcher
                    v-qa="'region-switcher'"
                    class="region-switcher"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { useMenus } from '@/plugins/menu'
import { useIsOnRoute } from '@/composables/useIsOnRoute'

import {
    ROUTE_PROFILE_DESIGN_STUDIO,
    ROUTE_PROFILE_MY_ORDERS,
    ROUTE_PROFILE_PRODUCTS,
} from '@/router'

import ThButton from '@/components/ui/buttons/ThButton.vue'
import CurrencySwitcher from './CurrencySwitcher.vue'
import RegionSwitcher from './RegionSwitcher.vue'
import CmsLink from '@/components/cms/CmsLink.vue'
import { useRoutes } from '@/router/routes'
import { useAuth } from '@/plugins/auth.ts'

const emit = defineEmits<{
    toggleLoginForm: []
    closeMenu: []
}>()

const routes = useRoutes()

const hideSwitchers = useIsOnRoute([
    ROUTE_PROFILE_DESIGN_STUDIO,
    ROUTE_PROFILE_MY_ORDERS,
    ROUTE_PROFILE_PRODUCTS,
])

const { topBar } = useMenus()
const toggleLoginForm = () => emit('toggleLoginForm')
const closeMenu = () => emit('closeMenu')

const { isLogged } = useAuth()

const { t } = useI18n()
</script>

<style lang="scss" scoped>
.top-bar {
    width: 100%;
    background: $blue;
    color: $white;
    height: 70px;
    z-index: 101;
    position: fixed;
    bottom: 0;
    padding: 20px;
    display: none;

    @include min-width(1024) {
        position: relative;
        height: 30px;
        padding: 0;
        display: block;
        bottom: initial;
        background: $stone-2;
        border-bottom: 1px solid $grey-200;
        color: $slate;
    }

    .top-bar-wrapper {
        width: 1440px;
        max-width: 100%;
        margin: 0 auto;
        display: flex;
        justify-content: space-between;

        @include min-width(1024) {
            padding: 0 40px;
        }
    }

    .switchers {
        display: flex;

        .region-switcher {
            margin-left: 16px;
        }
    }

    .book-a-consultation {
        font-family: $effra-font-stack;
        font-size: 0.8rem;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        color: $slate;
        line-height: 30px;
        padding: 0 15px;
        align-content: center;
        margin-right: 15px;
        display: none;

        @include min-width(1024) {
            display: flex;
        }

        &::before {
            display: block;
            font-family: $icon-font;
            content: $icon-location-pin;
            margin-right: 8px;
            font-weight: 600;
        }
    }

    .top-bar-account {
        color: $white;
        display: flex;
        align-items: center;

        @include min-width(1024) {
            display: none;
        }

        :deep(.th-button.link) {
            border-bottom: 0;
            padding: 0;
            display: flex;
            align-items: center;
            color: $white;

            .icon {
                font-size: 20px;
            }
        }
    }

    .site-wide-message {
        color: $slate;
        font-family: $effra-font-stack;
        text-transform: uppercase;
        font-size: 0.7rem;
        line-height: 30px;
        letter-spacing: 0.05em;
        font-weight: 400;
        margin: 0 auto 0 0;
        display: none;

        @include min-width(1024) {
            display: block;
        }
    }
}
</style>
